import '../../styles/restScreens.css';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import ModalSurface from '../shared/ModalSurface';
import ResponsiveRow from '../shared/ResponsiveRow';
import useViewModePreference from '../shared/useViewModePreference';
import ActionPillMenu from '../ui/ActionPillMenu';
import useModuleAccess from '../../hooks/useModuleAccess';
import EventCard from './EventCard';
import EventsListView from './EventsListView';
import EventsCalendarView from './EventsCalendarView';
import EventFormModal from './EventFormModal';
import EventsFoundationBar from './foundation/EventsFoundationBar';
import {
    BLANK_EVENT_FORM, EVENT_STATUS_FILTERS, EVENT_VIEW_MODES, EVENT_VIEW_STORAGE_KEY, EVENT_WHEN_FILTERS,
    FALLBACK_EVENT_TYPES, eventStatusLabel, eventTypeLabel, formFromEvent,
} from './eventConstants';
import {
    cancelEvent, createEvent, saveEventExtras, loadEventEntities, loadEventKits, loadEventRsvpSummary, loadEvents, loadTenantTimeZone, updateEvent,
} from './eventsApi';
import {
    DEFAULT_TENANT_ZONE, boardKpis, downloadCsv, eventsCsv, eventsCsvFileName, isCanceled, matchesSearch, matchesWhen, statusCounts, todayKey,
} from './eventsBoard';
import { DEFAULT_EVENT_SETTINGS, newEventForm } from './eventSettings';
import { loadEventSettingsOrDefaults } from './eventSettingsApi';
import { resolveEventWords } from './eventWords';
import { loadHotelBlocks } from './hotel/hotelApi';
import { loadAttendanceMap } from './checkin/checkinApi';
import {
    ACTION_PILL_CLASS, ACTION_PILL_QUIET_CLASS, GLASS_PILL_CLASS, GLASS_PILL_STYLE, INPUT_CLASS, PANEL_CLASS, TILE_CLASS, pillClass, segmentClass,
} from './eventStyles';

// "a Wedding, Corporate event, Social event or Nonprofit gala" from the company's own types (the starter names when none load).
function kitList(kits) {
    const names = kits.map((kit) => kit.label).filter(Boolean);
    if (names.length <= 1) return names.length ? `a ${names[0]}` : 'a type';
    return `a ${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
}

function KpiTile({ label, value, note, testId }) {
    return (
        <div className={`${TILE_CLASS} p-4 pt-5`} data-testid={testId}>
            <div className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-muted)]">{label}</div>
            <div className="mt-1 text-3xl font-bold leading-tight text-[var(--ink)]" data-testid={`${testId}-value`}>{value}</div>
            <div className="mt-1 text-xs text-[var(--ink-muted)]">{note}</div>
        </div>
    );
}

// Module 19 (BL-380) G1 - the Events board: every event on one screen as Cards, a List or a month Calendar.
// Phone-first: the toolbar wraps, the list becomes labeled cards, and the calendar swaps cell text for counts.
// UX pass: one labelled, connected choice row ("segmented control"). Exactly one option is pressed at a time.
export function FilterGroup({ id, label, options, value, onChange, description, testId }) {
    return (
        <div className="flex min-w-0 flex-col gap-1" data-testid={testId}>
            <span id={`${id}-label`} className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-muted)]">{label}</span>
            <div role="group" aria-labelledby={`${id}-label`} data-description={description}
                className="ia-pill-row self-start">
                {options.map((option) => {
                    const pressed = option.value === value;
                    return (
                        <button key={option.value} type="button" aria-pressed={pressed} onClick={() => onChange(option.value)}
                            data-testid={option.testId}
                            className={`${segmentClass(pressed)}`}>
                            {option.label}{option.count !== undefined && <>{' '}<span>{option.count}</span></>}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export default function EventsView({ userId, orgId, isUser = false, onOpenGuests, onOpenPlan, onOpenPrograms }) {
    // G8: Events has its own permissions area; planners never need Dispatch rights.
    const { allowed: eventsEditAllowed } = useModuleAccess(isUser ? 'events' : null, 'edit');
    const canEdit = !isUser || eventsEditAllowed;
    const [viewMode, setViewMode] = useViewModePreference({ userId, storageKey: EVENT_VIEW_STORAGE_KEY, modes: EVENT_VIEW_MODES, defaultMode: 'Cards' });

    const [events, setEvents] = useState([]);
    const [kits, setKits] = useState(FALLBACK_EVENT_TYPES);
    const [settings, setSettings] = useState(DEFAULT_EVENT_SETTINGS);
    const [entities, setEntities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [search, setSearch] = useState('');
    const [rsvp, setRsvp] = useState(null);          // Map of engagement_id -> RSVP counts, or null while / if they could not be read
    const [rsvpFailed, setRsvpFailed] = useState(false);
    const [zone, setZone] = useState(DEFAULT_TENANT_ZONE);
    const [hotels, setHotels] = useState(() => new Map()); // G6: engagement_id -> hotel block (one read for the board)
    const [attendance, setAttendance] = useState(null);    // G5: engagement_id -> check-in numbers (one read), or null
    const [statusFilter, setStatusFilter] = useState('planned');
    const [whenFilter, setWhenFilter] = useState('upcoming');

    const [form, setForm] = useState(null); // { mode, initial, engagementId }
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [confirmCancel, setConfirmCancel] = useState(null);
    const [cancelError, setCancelError] = useState('');

    const refresh = useCallback(async () => {
        setLoadError('');
        try {
            setEvents(await loadEvents(supabaseClient));
        } catch (error) {
            setLoadError(error?.message || 'We could not load your list.');
            setEvents([]);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        refresh();
        loadEventEntities(supabaseClient).then(setEntities).catch(() => setEntities([]));
        loadEventSettingsOrDefaults(supabaseClient).then(setSettings);
        loadEventKits(supabaseClient).then((rows) => { if (rows.length) setKits(rows); }).catch(() => {});
        // DC-1: the whole board's RSVP numbers in one read, and the company time zone for "today"
        loadEventRsvpSummary(supabaseClient).then((map) => { setRsvp(map); setRsvpFailed(false); }).catch(() => { setRsvp(null); setRsvpFailed(true); });
        loadTenantTimeZone(supabaseClient, orgId).then(setZone);
        loadHotelBlocks(supabaseClient).then(setHotels);
        loadAttendanceMap(supabaseClient).then(setAttendance);
    }, [refresh, orgId]);

    // Q-046: the company's own words ("Party", "Parties") on every line of this screen.
    const words = useMemo(() => resolveEventWords(settings), [settings]);

    // "Today" is the company's day, not the browser's.
    const today = todayKey(zone);
    const visible = useMemo(() => events.filter((event) => {
        if (statusFilter === 'planned' && isCanceled(event)) return false;
        if (statusFilter === 'canceled' && !isCanceled(event)) return false;
        return matchesWhen(event, whenFilter, today) && matchesSearch(event, search);
    }), [events, search, statusFilter, whenFilter, today]);
    const pillCounts = useMemo(() => statusCounts(events, { whenFilter, search, today }), [events, whenFilter, search, today]);
    const kpis = useMemo(() => boardKpis(events, rsvp, today), [events, rsvp, today]);

    const exportCsv = () => downloadCsv(
        eventsCsvFileName(today),
        eventsCsv(visible, rsvp, { typeLabel: (key) => eventTypeLabel(kits, key), statusLabel: eventStatusLabel }),
    );

    const soleEntityId = entities.length === 1 ? entities[0].id : '';
    const openCreate = (kit) => {
        setSaveError('');
        // A new event starts from Events Settings (default times, type and guest count); this event can change any of it.
        setForm({
            mode: 'create',
            initial: newEventForm({ ...BLANK_EVENT_FORM, entity_id: soleEntityId }, settings, kit, kits),
        });
    };
    const openEdit = (event) => {
        setSaveError('');
        setForm({ mode: 'edit', engagementId: event.engagement_id, initial: formFromEvent(event) });
    };

    const save = async (values) => {
        setSaving(true);
        setSaveError('');
        try {
            if (form.mode === 'edit') {
                await updateEvent(supabaseClient, form.engagementId, values);
                await saveEventExtras(supabaseClient, form.engagementId, values);
            } else {
                const created = await createEvent(supabaseClient, values);
                await saveEventExtras(supabaseClient, created, { ...values, guest_settings_loaded: false });
            }
            setForm(null);
            await refresh();
        } catch (error) {
            setSaveError(error?.message || `We could not save this ${words.eventLower}. Please try again.`);
        }
        setSaving(false);
    };

    const doCancel = async () => {
        setSaving(true);
        setCancelError('');
        try {
            await cancelEvent(supabaseClient, confirmCancel.engagement_id);
            setConfirmCancel(null);
            await refresh();
        } catch (error) {
            setCancelError(error?.message || `We could not cancel this ${words.eventLower}.`);
        }
        setSaving(false);
    };

    const newEventOptions = [
        ...kits.map((kit) => ({ key: kit.key, label: kit.label, onSelect: () => openCreate(kit) })),
        { key: '__blank', label: `Blank ${words.eventLower}`, onSelect: () => openCreate(null) },
    ];

    const listProps = { events: visible, kits, canEdit, rsvp, words, hotels: settings.hotel_block_enabled !== false ? hotels : null, attendance: settings.checkin_enabled !== false ? attendance : null, onGuests: onOpenGuests, onPlan: onOpenPlan, onEdit: openEdit, onCancel: (event) => { setCancelError(''); setConfirmCancel(event); } };

    return (
        <div className="ia-events" data-m6-layout="events" data-m6-component="EventsView">
            <ResponsiveRow className="flex flex-wrap justify-between items-center gap-3 mb-3">
                <div>
                    <h2 className="text-2xl font-bold text-[var(--ink)]" data-testid="events-title">{words.events}</h2>
                    <p className="text-sm text-[var(--ink-muted)]">Every {words.eventLower} you are planning, in one place.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button type="button" onClick={exportCsv} disabled={visible.length === 0} className={GLASS_PILL_CLASS} style={GLASS_PILL_STYLE} data-testid="events-export">Export</button>
                    {canEdit && (
                        <ActionPillMenu
                            menuLabel={`Start a new ${words.eventLower}`}
                            placement="bottom"
                            align="end"
                            options={newEventOptions}
                            onTriggerClick={() => openCreate(null)}
                            triggerClassName={ACTION_PILL_CLASS}
                        >
                            <span>+ New {words.event}</span>
                            <span aria-hidden="true">▾</span>
                        </ActionPillMenu>
                    )}
                </div>
            </ResponsiveRow>
            <div className="flow-rule h-px mb-4" aria-hidden="true" data-testid="events-rule" />
            <EventsFoundationBar canEdit={canEdit} onOpenPlan={onOpenPlan} onOpenPrograms={onOpenPrograms} />

            <div className={`${TILE_CLASS} p-3 pt-4 mb-4 flex flex-col gap-3`} data-testid="events-toolbar">
                <input
                    type="search"
                    aria-label={`Search ${words.eventsLower}`}
                    placeholder="Search by name or venue…"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    className={INPUT_CLASS}
                />
                {/* UX pass 2026-10-25 (Val: "clear the double selection"). Status and date are two separate questions, so each
                    is its own labelled row ("Status", "When") drawn as one connected control with exactly one choice lit.
                    Two lit pills now read as one answer per question, never as two picks in one group. View sits apart. */}
                <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end lg:gap-x-6" data-testid="events-filters">
                    <FilterGroup id="events-status" label="Status" testId="filter-group-status"
                        options={EVENT_STATUS_FILTERS.map((filter) => ({ value: filter.value, label: filter.label, count: pillCounts[filter.value], testId: `status-pill-${filter.value}` }))}
                        value={statusFilter} onChange={setStatusFilter} description={`Show ${words.eventsLower} by status`} />
                    <FilterGroup id="events-when" label="When" testId="filter-group-when"
                        options={EVENT_WHEN_FILTERS.map((filter) => ({ value: filter.value, label: filter.label }))}
                        value={whenFilter} onChange={setWhenFilter} description={`Show ${words.eventsLower} by date`} />
                    <div className="lg:ml-auto">
                        <FilterGroup id="events-view" label="View" testId="filter-group-view"
                            options={EVENT_VIEW_MODES.map((mode) => ({ value: mode, label: mode }))}
                            value={viewMode} onChange={setViewMode} description={`${words.events} view`} />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4" data-testid="events-kpis">
                <KpiTile testId="kpi-next30" label={`${words.events} next 30 days`} value={kpis.next30} note="In planning, from today for 30 days" />
                <KpiTile testId="kpi-invited" label="Guests invited" value={kpis.available ? kpis.invited : '—'} note={`Across upcoming ${words.eventsLower} in planning`} />
                <KpiTile testId="kpi-rate" label="RSVP response rate" value={kpis.rate === null ? '—' : `${kpis.rate}%`} note={`Answered ÷ invited, upcoming ${words.eventsLower} in planning`} />
            </div>

            {rsvpFailed && <div role="status" className="mb-4 rounded-lg border border-[var(--ui-border)] p-3 text-sm text-[var(--ink-muted)]" data-testid="rsvp-unavailable">RSVP numbers are not available right now. Your {words.eventsLower} are shown without them.</div>}

            {loadError && <div role="alert" className="mb-4 rounded-lg border border-[var(--ui-alert)] p-3 text-sm text-[var(--ink)]">Couldn’t load {words.eventsLower}: {loadError}</div>}

            {loading ? (
                <div className="p-8 text-center text-[var(--ink-muted)]">Loading…</div>
            ) : viewMode === 'Calendar' ? (
                <EventsCalendarView {...listProps} events={visible} />
            ) : visible.length === 0 ? (
                <div className={`${TILE_CLASS} p-10 text-center`}>
                    <div className="font-medium text-[var(--ink)]">{events.length === 0 ? `No ${words.eventsLower} yet` : 'Nothing matches those filters'}</div>
                    <div className="text-sm mt-1 text-[var(--ink-muted)]">
                        {events.length === 0 ? `Start with ${kitList(kits)} — or a blank ${words.eventLower}.` : 'Try another date range, status or search.'}
                    </div>
                </div>
            ) : viewMode === 'List' ? (
                <EventsListView {...listProps} />
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 min-[1100px]:grid-cols-3 gap-3" data-testid="events-cards">
                    {visible.map((event) => <EventCard key={event.id} event={event} {...listProps} />)}
                </div>
            )}

            {form && (
                <EventFormModal
                    client={supabaseClient}
                    engagementId={form.engagementId}
                    mode={form.mode}
                    initial={form.initial}
                    kits={kits}
                    entities={entities}
                    words={words}
                    canEdit={canEdit}
                    saving={saving}
                    error={saveError}
                    onSave={save}
                    onClose={() => setForm(null)}
                />
            )}

            {confirmCancel && (
                <ModalSurface
                    label={`Cancel this ${words.eventLower}?`}
                    onClose={() => setConfirmCancel(null)}
                    closeDisabled={saving}
                    busy={saving}
                    role="alertdialog"
                    panelProps={{ 'data-m6-layout': 'modals', 'data-m6-component': 'EventCancelDialog' }}
                    panelClassName="bg-[var(--surface-raised)] text-[var(--ink)] rounded-xl shadow-xl w-full max-w-md p-4 sm:p-6 space-y-4"
                >
                    <h2 className="text-lg font-bold">Cancel “{confirmCancel.event_name}”?</h2>
                    <p className="text-sm text-[var(--ink-muted)]">It stays on your board marked Canceled, so you keep the record.</p>
                    {cancelError && <div role="alert" className="rounded-lg border border-[var(--ui-alert)] p-3 text-sm">{cancelError}</div>}
                    <div className="flex flex-wrap justify-end gap-2">
                        <button type="button" onClick={() => setConfirmCancel(null)} disabled={saving} className={ACTION_PILL_QUIET_CLASS}>Keep {words.eventLower}</button>
                        <button type="button" onClick={doCancel} disabled={saving} className={`${ACTION_PILL_CLASS} disabled:opacity-50`}>{saving ? 'Canceling…' : `Cancel ${words.eventLower}`}</button>
                    </div>
                </ModalSurface>
            )}
        </div>
    );
}
